import {ClientId} from 'notebook/ELNConstants';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  BaseStructuredTable,
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {RowMetadata} from 'notebook/tables/TableConstants';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  ParameterUsage,
  ParameterUsages,
  ProcedureDataParameterSetting,
} from 'procedures/ProcedureDomainTypes';
import BaseProcedureDataParametersConfigTableAdapter, {
  BaseProcedureDataParametersConfigTableRowMetadata,
  buildCoreDataParameterSetting,
  ProcedureDataParametersConfigTableMetadata,
  isDataParameterSettingEmpty,
} from 'procedures/tables/BaseProcedureDataParametersConfigTableAdapter';
import {getParameterUsagesForId} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {Optional} from 'util/Types';

export interface ProcedureDataParametersConfigTableRowMetadata extends BaseProcedureDataParametersConfigTableRowMetadata {}

export type ProcedureDataParametersConfigTable = BaseStructuredTable<
  ProcedureDataParametersConfigTableMetadata,
  ProcedureDataParametersConfigTableRowMetadata,
  ClientId
>;

/**
 * This table adapter is for configuration of parameters
 */
export default class ProcedureDataParametersConfigTableAdapter extends BaseProcedureDataParametersConfigTableAdapter<ProcedureDataParametersConfigTableRowMetadata> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG, options);
  }

  getBlankRowMetadata(
    _tableMetadata: ProcedureDataParametersConfigTableMetadata
  ): ProcedureDataParametersConfigTableRowMetadata {
    return {apiId: null, procedureDataParameterId: null, plannedValueId: null};
  }

  static getAdapter(): ProcedureDataParametersConfigTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG
    ] as ProcedureDataParametersConfigTableAdapter;
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureDataParametersConfigTable,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>
  ): boolean {
    const setting = ProcedureDataParametersConfigTableAdapter.rowToDataParameterSetting(
      structuredTable.columns,
      row
    );
    return isDataParameterSettingEmpty(setting);
  }

  static dataParameterToRow(
    dataParameterSetting: ProcedureDataParameterSetting,
    table: ProcedureDataParametersConfigTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>,
    _cellLockedMessage?: Optional<string>
  ): StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata> {
    const adapter = ProcedureDataParametersConfigTableAdapter.getAdapter();

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: dataParameterSetting.dataParameterVersion.cid,
      metadata: {
        apiId: dataParameterSetting.dataParameterVersion.id,
        procedureDataParameterId: dataParameterSetting.dataParameterVersion.procedureDataParameterId,
        plannedValueId: dataParameterSetting.values.length > 0 ? dataParameterSetting.values[0].id : null,
      },
      cells: table.columns.map((column) =>
        BaseProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
          dataParameterSetting,
          column,
          validationErrors,
          parameterUsages
        )
      ),
    };
  }

  static rowToDataParameterSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>
  ): ProcedureDataParameterSetting {
    return buildCoreDataParameterSetting(columns, row);
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    dataParameterSetting: ProcedureDataParameterSetting,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    return columns.map((column) => {
      const delegatedColumnProvenanceId = (row.metadata as RowMetadata).delegatedColumns?.[
        column.provenanceUuid
      ];
      return {
        columnGroup: null,
        columnType: column.columnType,
        columnProvenanceUuid: delegatedColumnProvenanceId ?? column.provenanceUuid,
        submittedAt,
        fieldId: null,
        value:
          BaseProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
            dataParameterSetting,
            column,
            [],
            parameterUsages
          ).typedValue ?? null,
      };
    });
  }

  createTableFromDataParameters(
    createProps: CreateStructuredTableProps<ProcedureDataParametersConfigTableMetadata>,
    tableCid: string,
    dataParameters: Array<ProcedureDataParameterSetting>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages
  ): ProcedureDataParametersConfigTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: dataParameters.map((d, index) =>
        ProcedureDataParametersConfigTableAdapter.dataParameterToRow(
          d,
          baseTable,
          index,
          procedureValidationErrors,
          getParameterUsagesForId(parameterUsagesByParameterVersionId, d.dataParameterVersion.id),
          null
        )
      ),
    };
  }
}
